import { useState, useEffect } from "react";
import { Star, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { WriteReviewDialog } from "@/components/WriteReviewDialog";
import { Mascot } from "@/components/Mascot";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { usePromptQueue, PromptPriority } from "@/contexts/PromptQueueContext";

interface PostCheckInReviewPromptProps {
  cafeId: string;
  cafeName: string;
  checkInId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PostCheckInReviewPrompt({
  cafeId,
  cafeName,
  checkInId,
  open,
  onOpenChange,
}: PostCheckInReviewPromptProps) {
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);
  const { user } = useAuth();
  const { requestPrompt, releasePrompt, isActivePrompt } = usePromptQueue();

  // Only show the modal review prompt if the user is a repeat visitor (3+ check-ins)
  // Otherwise, save a nudge for the feed to show later
  useEffect(() => {
    if (!open || !user) return;

    const decide = async () => {
      // Check if the user already reviewed this cafe today
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { data: existingReview } = await supabase
        .from("reviews")
        .select("id")
        .eq("cafe_id", cafeId)
        .eq("user_id", user.id)
        .gte("created_at", oneDayAgo)
        .limit(1)
        .maybeSingle();

      if (existingReview) {
        onOpenChange(false);
        return;
      }

      // Check visit count at this cafe
      const { count } = await (supabase as any)
        .from("check_ins_visits")
        .select("id", { count: "exact", head: true })
        .eq("cafe_id", cafeId)
        .eq("user_id", user.id);

      const visits = count || 1;

      if (visits >= 3) {
        // Regular visitor — request queue slot
        setShouldShow(true);
        requestPrompt('post-checkin-review', PromptPriority.CHECK_IN);
      } else {
        // First or second visit — save a nudge for later (feed or next visit)
        try {
          const nudge = {
            cafeId,
            cafeName,
            checkInId,
            savedAt: new Date().toISOString(),
          };
          localStorage.setItem(`grounded_review_nudge_${user.id}`, JSON.stringify(nudge));
        } catch { /* localStorage full — fail silently */ }
        onOpenChange(false);
      }
    };

    decide();
  }, [open, user, cafeId, cafeName, checkInId, onOpenChange]);

  // Release the prompt slot on unmount so navigation/error paths don't leak it.
  useEffect(() => {
    return () => {
      releasePrompt('post-checkin-review');
    };
  }, [releasePrompt]);

  const handleWriteReview = () => {
    releasePrompt('post-checkin-review');
    setShouldShow(false);
    onOpenChange(false);
    // Small delay to let the prompt close before opening review dialog
    setTimeout(() => setShowReviewDialog(true), 150);
  };

  const handleDismiss = () => {
    releasePrompt('post-checkin-review');
    setShouldShow(false);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog
        open={open && shouldShow && isActivePrompt('post-checkin-review')}
        onOpenChange={(o) => {
          if (!o) {
            releasePrompt('post-checkin-review');
            setShouldShow(false);
          }
          onOpenChange(o);
        }}
      >
        <DialogContent className="max-w-sm p-0 gap-0 overflow-hidden border-0 shadow-xl">
          <VisuallyHidden>
            <DialogTitle>Share your experience</DialogTitle>
          </VisuallyHidden>
          
          {/* Decorative gradient header */}
          <div className="relative bg-gradient-to-br from-primary/20 via-primary/10 to-transparent p-6 pb-4">
            
            <div className="flex items-center gap-3 mb-3">
              <Mascot mood="happy" size="sm" />
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className="h-4 w-4 fill-caramel text-caramel animate-pulse"
                    style={{ animationDelay: `${star * 100}ms` }}
                  />
                ))}
              </div>
            </div>
            
            <h3 className="font-display text-lg font-semibold text-foreground">
              How was your visit?
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Your review helps other remote workers find great spots
            </p>
          </div>
          
          {/* Actions */}
          <div className="p-4 pt-3 space-y-2 bg-background">
            <Button 
              onClick={handleWriteReview}
              className="w-full gap-2 btn-cozy"
            >
              <Star className="h-4 w-4" />
              Write a Quick Review
              <ArrowRight className="h-4 w-4 ml-auto" />
            </Button>
            
            <Button 
              variant="ghost" 
              onClick={handleDismiss}
              className="w-full text-muted-foreground hover:text-foreground"
            >
              Maybe later
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Dialog - opens after prompt is dismissed */}
      <WriteReviewDialog
        cafeId={cafeId}
        cafeName={cafeName}
        checkInId={checkInId}
        open={showReviewDialog}
        onOpenChange={setShowReviewDialog}
        afterCheckIn
      />
    </>
  );
}
